import React from 'react';
import Grid from '@mui/material/Grid';
import InputAdornment from '@mui/material/InputAdornment';
import dayjs from 'dayjs';
import {
  DateEditor,
  NumberEditor,
  StripePriceEditor,
  TextAreaEditor,
  TextEditor,
  withStaticSelectEditor,
} from 'shared/components/data-display/editors';
import { tenantEntity } from 'shared/entityDetails';
import { AsyncSearchableSelect } from 'shared/components/select/SearchableSelects';
import { withStaticSelect } from 'shared/components/data-display/withSelectors';
import {
  couponTypeEnums,
  discountDurationEnums,
  discountTypeEnums,
  maxCouponIdLength,
  numberOfRedemptionEnums,
  redemptionCodeEnums,
} from './helper';
import Refiner from './Refiner';

const maxCouponDate = dayjs().add(5, 'year');

/**
 * @param {Object} props
 * @param {Object<string, *>} props.couponFormState
 * @param {string} props.couponType
 * @param {Function} props.handleChange
 * @param {Function} props.handleCouponTypeChange
 * @param {Function} props.handleRedeemByChange
 * @param {Object} props.minDate
 * @param {number} props.numResultsSelected
 * @param {string} props.refinerKey
 * @param {Object<string, Array<*>>} props.refinerResults
 * @param {Object<string, *>} props.selectedTenant
 * @param {Function} props.setPrice
 * @param {Function} props.setPercentage
 * @param {Function} props.setRefiner
 * @param {Function} props.setTenant
 * @param {Function} props.setValue
 * @param {Function} props.submitCoupon
 */
export default function CouponForm(props) {
  const {
    couponFormState,
    couponType,
    handleChange,
    handleCouponTypeChange,
    handleRedeemByChange,
    numResultsSelected,
    refinerKey,
    refinerResults,
    selectedTenant,
    setPrice,
    setPercentage,
    setRefiner,
    setTenant,
    setValue,
    submitCoupon,
    minDate,
  } = props;

  const {
    amountOff,
    couponId,
    description,
    discountType,
    duration,
    durationInMonths,
    idType,
    maxRedemption,
    percentOff,
    redeemBy,
    redemptionType,
  } = couponFormState;

  let picker = null;
  if (couponType === couponTypeEnums.campaign) {
    picker = (
      <div className="non-input-form">
        <Refiner
          modelKey={refinerKey}
          results={refinerResults}
          numResultsSelected={numResultsSelected}
          setRefiner={setRefiner}
        />
      </div>
    );
  } else if (couponType === couponTypeEnums.tenant) {
    picker = (
      <AsyncSearchableSelect
        baseSearch="auth_method=802.1x&ob=it_firstname"
        defaultOptions={false}
        label="Select Tenant"
        getOptionLabel={tenantEntity.getOptionLabel}
        onChange={setTenant}
        searchRequest={tenantEntity.searchRequest}
        selectedOption={selectedTenant.id && selectedTenant}
      />
    );
  }

  return (
    <form id="coupon-form" onSubmit={submitCoupon}>
      <Grid container spacing={2}>
        <Grid size={{ xs: 12, md: 12, lg: 6 }}>
          {withStaticSelect({
            options: Object.values(couponTypeEnums),
            valueAsLabel: true,
          })({
            label: 'Coupon Type', setValue: handleCouponTypeChange, value: couponType, fullWidth: true,
          })}
          {withStaticSelectEditor(Object.values(discountTypeEnums))({
            label: 'Discount Type', setValue: handleChange('discountType'), value: discountType, fullWidth: true,
          })}
          {discountType === discountTypeEnums.percentOff
            ? (
              <NumberEditor
                maximum={100}
                minimum={1}
                value={percentOff}
                setValue={setPercentage}
                label="Percent Off"
                endAdornment={<InputAdornment position="end">%</InputAdornment>}
                required
              />
            ) : ( // discountType === discountTypeEnums.amountOff
              <StripePriceEditor
                required
                value={amountOff}
                setValue={setPrice}
                label="Amount Off"
              />
            )}
          {withStaticSelectEditor(Object.values(redemptionCodeEnums))({
            label: 'Redemption Code', setValue: handleChange('idType'), value: idType, fullWidth: true,
          })}
          <TextEditor
            required
            disabled={idType === redemptionCodeEnums.auto}
            slotProps={{
              htmlInput: {
                sx: { backgroundColor: 'inputBackground' },
                maxLength: maxCouponIdLength,
              },
              inputLabel: { shrink: true },
            }}
            minLength="8"
            label="Coupon Id"
            setValue={handleChange('couponId')}
            pattern="^[a-zA-Z0-9_]+$"
            placeholder={idType === redemptionCodeEnums.auto ? 'Stripe will create a random code' : ''}
            value={idType === redemptionCodeEnums.auto ? '' : couponId}
          />
          <TextAreaEditor setValue={(value) => setValue({ name: 'description', value })} value={description} label="Description" />
        </Grid>
        <Grid size={{ xs: 12, md: 12, lg: 6 }}>
          {picker}
          {withStaticSelectEditor(Object.values(discountDurationEnums))({
            label: 'Discount Duration', setValue: handleChange('duration'), value: duration, fullWidth: true,
          })}
          <NumberEditor
            disabled={duration !== discountDurationEnums.repeating}
            maximum={12}
            minimum={2}
            label="Duration In Months"
            setValue={handleChange('durationInMonths')}
            value={duration === discountDurationEnums.repeating ? durationInMonths : '1'}
          />
          {withStaticSelectEditor(Object.values(numberOfRedemptionEnums))({
            label: 'Number of Redemptions', disabled: (couponType === couponTypeEnums.tenant), setValue: handleChange('redemptionType'), value: redemptionType, fullWidth: true,
          })}
          <NumberEditor
            disabled={couponType === couponTypeEnums.tenant
                    || redemptionType === numberOfRedemptionEnums.unlimited}
            minimum={1}
            label="Max Redemption"
            setValue={handleChange('maxRedemption')}
            value={redemptionType === numberOfRedemptionEnums.unlimited ? '' : maxRedemption}
          />
          <DateEditor
            setValue={handleRedeemByChange}
            value={redeemBy}
            minDateStrVal={minDate}
            maxDate={maxCouponDate}
            label="Must be redeemed by"
          />
        </Grid>
      </Grid>
    </form>
  );
}
